import type { Theme } from '@mui/material';

import { STATUS_WAIT_SERVER_APPROVE } from '../../PlayerOverview/constants/comments';
import { URL } from '../constants/regExps';

import { CommentType } from '@/modules/common/types/comments';

import { getBgColor } from '@/mui/helpers';

export const getFormattedComment = (comment: Partial<CommentType>): CommentType => ({
  ...comment,
  author: {
    id: comment.author?.id ?? '',
    firstName: comment.author?.firstName ?? '',
    lastName: comment.author?.lastName ?? '',
  },
  activeChildCount: comment.activeChildCount ?? 0,
  deletedChildCount: comment.deletedChildCount ?? 0,
  time: comment.time ?? 0,
  id: comment.id ?? `${Math.random()}`,
  parentId: comment.parentId ?? undefined,
  status: comment.status ?? STATUS_WAIT_SERVER_APPROVE,
  created: comment.created ?? Date.now(),
  updated: comment.updated ?? undefined,
  updateCount: comment.updateCount ?? 0,
  html: comment.html ?? '',
});

export const getText = (html: string) => {
  const div = document.createElement('div');

  div.innerHTML = html;

  return div.textContent || '';
};

export const getMentions = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;

  return Array.from(div.querySelectorAll('[data-mention-id]')).map((el) => ({
    id: el.getAttribute('data-mention-id'),
  }));
};

export const getLinks = (html: string) => {
  const div = document.createElement('div');
  div.innerHTML = html;

  return Array.from(div.querySelectorAll('a')).map((el) => ({
    href: el.href,
  }));
};

const validateUrl = (html: string) => {
  const node = document.createElement('div');
  node.innerHTML = html;

  const linkNodes = node.querySelectorAll('a');

  linkNodes.forEach((link) => {
    const linkHref = link.getAttribute('href');

    if (linkHref) {
      if (URL.test(linkHref) && !linkHref.startsWith('http')) {
        // eslint-disable-next-line no-param-reassign
        link.href = `https://${linkHref}`;
      } else if (!URL.test(linkHref) && !linkHref.startsWith('mailto:')) {
        node.innerHTML = node.innerHTML.replace(link.outerHTML, link.innerHTML);
      }
    }
  });

  return node.innerHTML;
};

const unlinkMentions = (html: string) => {
  const node = document.createElement('div');

  node.innerHTML = html;

  const linkNodes = node.querySelectorAll('a');

  linkNodes.forEach((link) => {
    const mentionNodes = link.querySelectorAll('[data-mention-id]');

    if (mentionNodes.length) {
      const linkParentNode = link.parentNode;

      if (linkParentNode) {
        mentionNodes.forEach((mention) => {
          const space = document.createTextNode('\u00A0');
          linkParentNode.insertBefore(mention, link);
          linkParentNode.insertBefore(space, link);
        });
      }

      if (link) {
        if (!link.textContent?.trim()) {
          // eslint-disable-next-line no-param-reassign
          link.textContent = link.href;
        }
      }
    }
  });

  return node.innerHTML;
};

export const sanitizeContent = (html: string) => {
  const comment = document.createElement('div');
  const commentRes = document.createElement('div');

  comment.innerHTML = html;
  commentRes.innerHTML = validateUrl(unlinkMentions(comment.innerHTML));

  return comment.innerHTML !== commentRes.innerHTML ? commentRes.innerHTML : html;
};

const toBinary = (stringSource: string) => {
  let result = '';

  const str = encodeURIComponent(stringSource);

  for (let i = 0; i < str.length; i += 1) {
    if (str[i] === '%') {
      result += String.fromCharCode(parseInt(str.substring(i + 1, i + 3), 16));
      i += 2;
    } else result += str[i];
  }

  return result;
};

export const getUserAvatarURL = (id: string | number, firstName: string, lastName: string, theme: Theme) => {
  const viewBox = 28;
  const viewBoxCenter = viewBox / 2;
  const backgroundHex = getBgColor(`${id}${firstName}${lastName}`);
  const textColor = theme.palette.getContrastText(backgroundHex);
  const [firstLetter] = firstName || ' ';
  const [lastLetter] = lastName || ' ';

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttributeNS(null, 'viewBox', `0 0 ${viewBox} ${viewBox}`);
  svg.innerHTML = `
<circle r="${viewBoxCenter}" fill="${backgroundHex}" stroke="#ffffff" stroke-width="1px" cx="50%" cy="50%" />
<text
  fill="${textColor}"
  font-family="Roboto,sans-serif"
  font-size="12px"
  font-weight="400"
  alignment-baseline="middle"
  text-anchor="middle"
  x="50%"
  y="${viewBoxCenter + 1}">
${firstLetter.toUpperCase()}${!!firstLetter && !!lastLetter ? '<tspan style="font-size:3px;opacity:0;"></tspan>' : ''}${lastLetter.toUpperCase()}
</text>`;

  const encodedBase64 = window.btoa(toBinary(new XMLSerializer().serializeToString(svg)));
  return `data:image/svg+xml;base64,${encodedBase64}`;
};
